import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export const LANGUAGE_STORAGE_KEY = 'oikra_lang';

export const SUPPORTED_LANGUAGES = ['es', 'en', 'pt'];

function readStoredLanguage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const v = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return v === 'en' || v === 'es' || v === 'pt' ? v : null;
  } catch {
    return null;
  }
}

// Each language's translation dict lives in its own webpack chunk (~150-170KB
// of JSON before gzip) instead of all three being bundled into every page
// load. Only the active language is fetched up front; the others load on
// demand when the user actually switches.
const localeLoaders = {
  en: () => import(/* webpackChunkName: "i18n-en" */ './locales/en'),
  es: () => import(/* webpackChunkName: "i18n-es" */ './locales/es'),
  pt: () => import(/* webpackChunkName: "i18n-pt" */ './locales/pt'),
};

const loadedLanguages = new Set();

async function loadLanguage(lng) {
  if (loadedLanguages.has(lng)) return;
  const mod = await localeLoaders[lng]();
  i18n.addResourceBundle(lng, 'translation', mod.default, true, true);
  loadedLanguages.add(lng);
}

/**
 * Persist UI language and switch i18n (ES / EN / PT), lazy-loading that
 * language's chunk first if it hasn't been fetched yet.
 * @param {string} lng
 * @returns {Promise<void>}
 */
export async function setAppLanguage(lng) {
  if (lng !== 'en' && lng !== 'es' && lng !== 'pt') return;
  await loadLanguage(lng);
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
  } catch {
    /* ignore quota / private mode */
  }
  await i18n.changeLanguage(lng);
}

const initialLanguage = readStoredLanguage() || 'es';

/**
 * Resolves once i18next is initialized with the active language's resources
 * loaded. Callers that render translated text (index.js) must await this
 * before the first render — i18next has no resources at all until then.
 */
export const i18nReady = (async () => {
  // Kick off the chunk fetch in parallel with init() — addResourceBundle
  // isn't available on the instance until after init() resolves, but the
  // network/chunk fetch itself doesn't depend on that.
  const initialModulePromise = localeLoaders[initialLanguage]();

  if (!i18n.isInitialized) {
    await i18n.use(initReactI18next).init({
      resources: {},
      lng: initialLanguage,
      fallbackLng: {
        pt: ['en'],
        es: ['en'],
        default: ['en'],
      },
      interpolation: {
        escapeValue: false,
      },
    });
  }

  const mod = await initialModulePromise;
  i18n.addResourceBundle(initialLanguage, 'translation', mod.default, true, true);
  loadedLanguages.add(initialLanguage);

  // Fallback chain depends on 'en' being present. Defer the fetch to idle
  // time — firing it immediately at boot competes with the initial page's
  // own critical JS/CSS for bandwidth and main-thread time (measurably
  // worse LCP/TBT), and the fallback is only needed for occasional missing
  // keys, never for first paint.
  if (initialLanguage !== 'en') {
    const prefetchEnFallback = () => {
      loadLanguage('en').catch(() => {
        /* best-effort background fetch */
      });
    };
    if (typeof window !== 'undefined' && window.requestIdleCallback) {
      window.requestIdleCallback(prefetchEnFallback, { timeout: 5000 });
    } else {
      setTimeout(prefetchEnFallback, 3000);
    }
  }
})();

export default i18n;
